import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CP, CapCard, Pill, InitialAvatar, HBar } from "./ui";
import { Robot, PaperPlaneTilt, ArrowClockwise, Sparkle, UsersThree } from "@phosphor-icons/react";

const STARTER_PROMPTS = [
  "Migrate our data lake to Databricks with governance, 6 weeks, 3 people",
  "Assemble a rapid response team for a P0 payments outage RCA",
  "Build an AI-powered churn prediction pipeline, 2 months",
  "I need reviewers for a Kubernetes hardening audit",
];

export default function TeamAdvisor() {
  const [sessionId, setSessionId] = useState(null);
  const [turns, setTurns] = useState([]);
  const [understanding, setUnderstanding] = useState({});
  const [lastQuestions, setLastQuestions] = useState([]);
  const [lastRecommendation, setLastRecommendation] = useState(null);
  const [quickReplies, setQuickReplies] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [turns, busy]);

  const send = async (rawText) => {
    const text = (rawText ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setTurns((t) => [...t, { role: "user", text }]);
    setLastQuestions([]);
    setQuickReplies([]);
    try {
      const { data } = await api.post("/capability/team/advise", { session_id: sessionId, message: text });
      setSessionId(data.session_id);
      setUnderstanding(data.understanding || {});
      setLastQuestions(data.clarifying_questions || []);
      setLastRecommendation(data.team_recommendation || null);
      setQuickReplies(data.quick_replies || []);
      setTurns((t) => [...t, { role: "assistant", text: data.assistant_text }]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
      setTurns((t) => [...t, { role: "assistant", text: "(advisor error — try again)" }]);
    } finally { setBusy(false); }
  };

  const reset = () => {
    setSessionId(null); setTurns([]); setUnderstanding({});
    setLastQuestions([]); setLastRecommendation(null); setQuickReplies([]); setInput(""); setShowBuilder(false);
  };

  return (
    <div className="space-y-4" data-testid="team-advisor">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl bg-white shadow-sm flex flex-col" style={{ border: `1px solid ${CP.border}`, minHeight: 600 }}>
          <div className="p-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${CP.border}` }}>
            <div className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#EFF4FF" }}>
                <Robot size={20} color={CP.blue} weight="duotone" />
              </span>
              <div>
                <div className="text-sm font-semibold" style={{ color: CP.text }}>AI Team Advisor</div>
                <div className="text-[10px]" style={{ color: CP.sub }}>Claude Sonnet 4.5 · grounded in your marketplace</div>
              </div>
            </div>
            <button onClick={reset} className="h-8 px-3 rounded-lg text-xs font-semibold flex items-center gap-1" style={{ border: `1px solid ${CP.border}`, color: CP.sub }} data-testid="advisor-reset">
              <ArrowClockwise size={13} /> Reset
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="advisor-messages">
            {turns.length === 0 && (
              <div className="text-center py-10 space-y-4">
                <div className="text-sm max-w-md mx-auto" style={{ color: CP.sub }}>
                  Describe the project or capability need. I&rsquo;ll interview you, propose a team from real people, and explain how to close remaining gaps.
                </div>
                <div className="flex flex-wrap gap-2 justify-center max-w-2xl mx-auto">
                  {STARTER_PROMPTS.map((p) => (
                    <button key={p} onClick={() => send(p)} data-testid="advisor-starter"
                      className="text-xs px-3 py-2 rounded-lg text-left max-w-xs hover:bg-slate-50 transition-colors"
                      style={{ border: `1px solid ${CP.border}`, color: CP.text }}>{p}</button>
                  ))}
                </div>
              </div>
            )}

            {turns.map((t, i) => (
              <div key={i} className={`flex ${t.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`advisor-turn-${i}`}>
                <div className="max-w-[85%] px-3.5 py-2.5 text-sm whitespace-pre-wrap rounded-xl"
                  style={t.role === "user"
                    ? { background: CP.navy, color: "#fff" }
                    : { background: "#F5F7FC", color: CP.text, border: `1px solid ${CP.border}` }}>
                  {t.text}
                </div>
              </div>
            ))}

            {busy && (
              <div className="flex justify-start" data-testid="advisor-thinking">
                <div className="px-3.5 py-2.5 rounded-xl flex gap-1" style={{ background: "#F5F7FC", border: `1px solid ${CP.border}` }}>
                  {[0, 150, 300].map((d) => (
                    <span key={d} className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: CP.sub, animationDelay: `${d}ms` }} />
                  ))}
                </div>
              </div>
            )}

            {lastQuestions.length > 0 && !busy && (
              <div className="space-y-2 pt-1" data-testid="advisor-questions">
                {lastQuestions.map((q, qi) => (
                  <div key={qi} className="rounded-lg p-3 space-y-2" style={{ border: `1px dashed #BFD3FE`, background: "#FBFCFF" }}>
                    <div className="text-xs font-semibold" style={{ color: CP.text }}>{q.question}</div>
                    <div className="flex flex-wrap gap-2">
                      {(q.options || []).map((opt, oi) => (
                        <button key={oi} onClick={() => send(opt)} data-testid={`advisor-option-${qi}-${oi}`}
                          className="text-xs px-3 py-1.5 rounded-full hover:opacity-80 transition-opacity"
                          style={{ background: "#EFF4FF", color: CP.blue, border: "1px solid #BFD3FE" }}>{opt}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {quickReplies.length > 0 && !busy && (
              <div className="flex flex-wrap gap-2 pt-1" data-testid="advisor-quick-replies">
                {quickReplies.map((qr, i) => (
                  <button key={i} onClick={() => send(qr)} data-testid={`advisor-quick-${i}`}
                    className="text-[11px] px-2.5 py-1 rounded-full hover:bg-slate-50"
                    style={{ border: `1px solid ${CP.border}`, color: CP.sub }}>{qr}</button>
                ))}
              </div>
            )}
          </div>

          <div className="p-3 flex items-center gap-2" style={{ borderTop: `1px solid ${CP.border}` }}>
            <input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Describe your project, timeline, constraints…" disabled={busy} data-testid="advisor-input"
              className="flex-1 h-10 px-3 rounded-lg text-sm bg-white outline-none"
              style={{ border: `1px solid ${CP.border}`, color: CP.text }} />
            <button onClick={() => send()} disabled={busy || !input.trim()} data-testid="advisor-send"
              className="h-10 px-4 rounded-lg text-sm font-semibold text-white flex items-center gap-1.5 disabled:opacity-50"
              style={{ background: CP.blue }}>
              <PaperPlaneTilt size={14} /> Send
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <CapCard title="Live Brief" testid="advisor-brief">
            {understanding?.objective ? (
              <div className="space-y-3 text-sm">
                <BriefRow label="Objective" value={understanding.objective} />
                {understanding.timeline && <BriefRow label="Timeline" value={understanding.timeline} />}
                {understanding.team_size_hint && <BriefRow label="Team size" value={understanding.team_size_hint} />}
                {understanding.must_have_skills?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: CP.sub }}>Must-have skills</div>
                    <div className="flex flex-wrap gap-1">{understanding.must_have_skills.map((s) => <Pill key={s} tone="blue">{s}</Pill>)}</div>
                  </div>
                )}
                {understanding.nice_to_have_skills?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: CP.sub }}>Nice to have</div>
                    <div className="flex flex-wrap gap-1">{understanding.nice_to_have_skills.map((s) => <Pill key={s} tone="slate">{s}</Pill>)}</div>
                  </div>
                )}
                {understanding.constraints?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: CP.sub }}>Constraints</div>
                    <ul className="text-xs list-disc pl-4 space-y-0.5" style={{ color: CP.text }}>
                      {understanding.constraints.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            ) : <div className="text-xs" style={{ color: CP.sub }}>As we chat, the advisor captures the brief here in real-time.</div>}
          </CapCard>
        </div>
      </div>

      {lastRecommendation && (
        <RecommendationCard rec={lastRecommendation} understanding={understanding}
          onCustomize={() => setShowBuilder(true)} />
      )}

      {showBuilder && <TeamBuilder initialSkills={(understanding.must_have_skills || []).join(", ")} />}
    </div>
  );
}

function BriefRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider" style={{ color: CP.sub }}>{label}</div>
      <div className="text-sm" style={{ color: CP.text }}>{value}</div>
    </div>
  );
}

/* --------- Screen 5: AI Recommended Team card --------- */
function RecommendationCard({ rec, understanding, onCustomize }) {
  const navigate = useNavigate();
  const whyBullets = [
    rec.why_it_fits,
    ...(rec.members || []).map((m) => m.why).filter(Boolean).slice(0, 3),
  ].filter(Boolean);

  return (
    <div className="rounded-xl bg-white shadow-sm p-5" style={{ border: `2px solid #BFD3FE` }} data-testid="advisor-recommendation">
      <div className="flex flex-wrap items-center gap-2">
        <Sparkle size={18} color={CP.blue} weight="fill" />
        <div className="text-base font-bold" style={{ color: CP.text }}>AI Recommended Team</div>
        {understanding?.objective && <span className="text-xs" style={{ color: CP.sub }}>Based on: {understanding.objective}</span>}
        {rec.confidence_pct !== undefined && (
          <span className="ml-auto"><Pill tone="green">Confidence {rec.confidence_pct}%</Pill></span>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mt-4">
        {(rec.members || []).map((m) => (
          <button key={m.user_id} onClick={() => navigate(`/profile/${m.user_id}`)} data-testid={`advisor-member-${m.user_id}`}
            className="flex flex-col items-center rounded-xl p-3 w-[130px] hover:shadow-md transition-shadow"
            style={{ border: `1px solid ${CP.border}` }}>
            <InitialAvatar name={m.name} size={44} />
            <div className="text-xs font-semibold mt-2 text-center" style={{ color: CP.text }}>{m.name}</div>
            <div className="text-[10px] text-center" style={{ color: CP.blue }}>{m.role_in_team}</div>
            <div className="text-[10px] mt-1" style={{ color: CP.sub }}>{m.workload_pct ?? 0}% load</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
        <div>
          <div className="text-sm font-semibold mb-2" style={{ color: CP.text }}>Why this team?</div>
          <ul className="space-y-1.5">
            {whyBullets.map((b, i) => (
              <li key={i} className="text-xs flex gap-2" style={{ color: CP.text }}>
                <span className="mt-0.5 shrink-0" style={{ color: CP.green }}>✓</span>{b}
              </li>
            ))}
          </ul>
          {(rec.gap_closure_plan || []).length > 0 && (
            <div className="mt-4">
              <div className="text-sm font-semibold mb-2" style={{ color: CP.text }}>Gap closure plan</div>
              <div className="space-y-1.5">
                {rec.gap_closure_plan.map((g, i) => (
                  <div key={i} className="text-xs flex gap-2 items-start" data-testid={`advisor-gap-${i}`}>
                    <span className="inline-block w-1.5 h-1.5 mt-1.5 rounded-full shrink-0"
                      style={{ background: g.severity === "high" ? CP.red : g.severity === "medium" ? CP.amber : CP.green }} />
                    <div><span className="font-semibold capitalize" style={{ color: CP.text }}>{g.gap}</span>
                      <span style={{ color: CP.sub }}> — {g.action}</span></div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div>
          <div className="text-sm font-semibold mb-2" style={{ color: CP.text }}>Skills Coverage</div>
          <div className="space-y-2.5">
            {(rec.skills_coverage || []).map((c) => (
              <HBar key={c.skill} label={c.skill} value={c.pct} max={100} suffix="%"
                color={c.pct >= 90 ? CP.green : c.pct >= 60 ? CP.blue : CP.amber} />
            ))}
            {(rec.skills_coverage || []).length === 0 && <div className="text-xs" style={{ color: CP.sub }}>Coverage computed once skills are confirmed.</div>}
          </div>
          <button onClick={onCustomize} data-testid="advisor-customize-btn"
            className="mt-5 h-10 px-5 rounded-lg text-sm font-semibold text-white w-full"
            style={{ background: CP.blue }}>Review &amp; Customize Team</button>
        </div>
      </div>
    </div>
  );
}

/* --------- Manual team builder (customize flow) --------- */
function TeamBuilder({ initialSkills }) {
  const [skills, setSkills] = useState(initialSkills || "python, kubernetes, data engineering");
  const [size, setSize] = useState(3);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const build = async () => {
    setBusy(true);
    try {
      const payload = { required_skills: skills.split(",").map((s) => s.trim()).filter(Boolean), team_size: size };
      const { data } = await api.post("/capability/team", payload);
      setResult(data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  useEffect(() => { build(); /* eslint-disable-line */ }, []);

  return (
    <CapCard title="Customize Team" subtitle="Adjust skills and size, rebuilt from evidence scores" testid="team-formation">
      <div className="flex flex-col sm:flex-row gap-3 items-end mb-4">
        <div className="flex-1 w-full">
          <div className="text-xs mb-1" style={{ color: CP.sub }}>Required capabilities</div>
          <input value={skills} onChange={(e) => setSkills(e.target.value)} data-testid="team-skills-input"
            className="w-full h-10 px-3 rounded-lg text-sm bg-white outline-none" style={{ border: `1px solid ${CP.border}`, color: CP.text }} />
        </div>
        <div>
          <div className="text-xs mb-1" style={{ color: CP.sub }}>Team size</div>
          <input type="number" min={1} max={10} value={size} onChange={(e) => setSize(Number(e.target.value))} data-testid="team-size-input"
            className="w-24 h-10 px-3 rounded-lg text-sm bg-white outline-none" style={{ border: `1px solid ${CP.border}`, color: CP.text }} />
        </div>
        <button onClick={build} disabled={busy} data-testid="team-build-btn"
          className="h-10 px-5 rounded-lg text-sm font-semibold text-white flex items-center gap-1.5 disabled:opacity-50" style={{ background: CP.navy }}>
          <UsersThree size={15} /> {busy ? "Building…" : "Rebuild team"}
        </button>
      </div>

      {result && (
        <div className="space-y-4" data-testid="team-summary">
          <div className="flex flex-wrap gap-6">
            <div><div className="text-[10px] uppercase tracking-wider" style={{ color: CP.sub }}>Coverage</div>
              <div className="text-2xl font-bold" style={{ color: CP.blue }}>{result.coverage_pct}%</div></div>
            <div><div className="text-[10px] uppercase tracking-wider" style={{ color: CP.sub }}>Team</div>
              <div className="text-2xl font-bold" style={{ color: CP.text }}>{result.team_size}/{result.requested_size}</div></div>
            {result.gaps.length > 0 && (
              <div><div className="text-[10px] uppercase tracking-wider" style={{ color: CP.sub }}>Gaps</div>
                <div className="flex gap-1 mt-1">{result.gaps.map((g) => <Pill key={g} tone="red">{g}</Pill>)}</div></div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {result.team.map((m) => (
              <button key={m.user_id} onClick={() => navigate(`/profile/${m.user_id}`)} data-testid={`team-member-${m.user_id}`}
                className="rounded-lg p-3 text-left hover:shadow-md transition-shadow flex items-center gap-3" style={{ border: `1px solid ${CP.border}` }}>
                <InitialAvatar name={m.name} size={36} />
                <div className="flex-1">
                  <div className="text-sm font-semibold" style={{ color: CP.text }}>{m.name}</div>
                  <div className="text-[11px]" style={{ color: CP.sub }}>{m.role} · {m.workload_pct}% load</div>
                </div>
                <div className="text-lg font-bold" style={{ color: CP.blue }}>{m.score}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </CapCard>
  );
}
